/**
 * Builders shared by the talent trees. Talents only ever use `ClassTransform`s, so every node
 * either changes a button (cooldown, cast, cost, targets, extra effect) or adds a proc.
 *
 * Sim facts the trees rely on (packages/sim/src/combat):
 *  - A `cast` proc fires BEFORE the ability's own effects run, so a "your next X is empowered"
 *    buff is consumed on `damageDealt` / `healDealt` instead, after the hit lands.
 *  - `damageDealt` / `healDealt` / `critDealt` fire once per unit hit, with the proc's target set
 *    to that unit, which is how "per enemy hit" talents are written.
 *  - Proc effects are attributed to the aura id, so they never re-trigger ability-filtered procs.
 */
import type { AbilityId, AreaSpec, AuraDef, AuraId, ClassId, Condition, EffectSpec, Ms, ProcSpec, StatMod } from '@keystone/sim';
import type { ClassTransform, TalentId, TalentNode } from '../../types';

export type NodeInput = Omit<TalentNode, 'maxRank' | 'prereqs' | 'classId'> & { prereqs?: TalentId[] };

/** Node factory bound to a class. */
export const nodeFor =
  (classId: ClassId) =>
  (n: NodeInput): TalentNode => ({ prereqs: [], maxRank: 1, classId, ...n });

/** A permanent hidden passive (compile forces persistThroughDeath). */
export function passive(id: AuraId, name: string, extra: Partial<AuraDef>): ClassTransform {
  return {
    op: 'addPassive',
    aura: { id, name, kind: 'buff', durationMs: 0, stacking: 'unique', flags: { hidden: true, persistThroughDeath: true }, ...extra },
  };
}

/** A passive that only carries procs. */
export const procPassive = (id: AuraId, name: string, ...procs: ProcSpec[]): ClassTransform => passive(id, name, { procs });

/** A passive that only carries ability-scoped mods (e.g. critChanceBonus on one ability). */
export const modPassive = (id: AuraId, name: string, mods: StatMod[]): ClassTransform => passive(id, name, { mods });

/** Self-applied timed buff. */
export function selfAura(aura: Omit<AuraDef, 'kind' | 'stacking'> & Partial<Pick<AuraDef, 'kind' | 'stacking'>>): EffectSpec {
  return { type: 'applyAura', to: 'self', aura: { kind: 'buff', stacking: 'refresh', ...aura } };
}

/** Hidden self marker used as an internal cooldown for an ability-embedded effect. */
export function icdMarker(id: AuraId, name: string, durationMs: Ms): EffectSpec {
  return { type: 'applyAura', to: 'self', aura: { id, name, kind: 'buff', durationMs, stacking: 'ignore', flags: { hidden: true } } };
}

/** "Do `then` if the ICD marker is down, then start the ICD." */
export function withIcd(id: AuraId, name: string, durationMs: Ms, cond: Condition | null, then: EffectSpec[]): EffectSpec {
  const gate: Condition = { type: 'selfLacksAura', auraId: id };
  return {
    type: 'conditional',
    cond: cond ? { type: 'and', all: [gate, cond] } : gate,
    then: [...then, icdMarker(id, name, durationMs)],
  };
}

export const resource = (res: string, amount: number): EffectSpec => ({ type: 'resource', to: 'self', resource: res, amount });

export const resetCd = (abilityId: AbilityId): EffectSpec => ({ type: 'modifyCooldown', to: 'self', abilityId, reset: true });

export const reduceCd = (abilityId: AbilityId, deltaMs: Ms): EffectSpec => ({ type: 'modifyCooldown', to: 'self', abilityId, deltaMs: -Math.abs(deltaMs) });

/** Circle around the primary target. */
export const nearTarget = (radius: number, maxTargets: number, affects: AreaSpec['affects'] = 'enemies', excludePrimary = true): AreaSpec => ({
  anchor: 'target',
  shape: { type: 'circle', radius },
  affects,
  maxTargets,
  ...(excludePrimary ? { excludePrimary: true } : {}),
});

/** The N most injured allies within `radius` of the caster (smart heal). */
export const injuredAllies = (radius: number): AreaSpec => ({ anchor: 'caster', shape: { type: 'circle', radius }, affects: 'allies' });

/** Magic schools, for "−X% magic damage taken" mods. */
export const MAGIC_SCHOOLS = ['holy', 'fire', 'nature', 'frost', 'shadow', 'arcane'] as const;
export const magicDamageTaken = (value: number): StatMod[] => MAGIC_SCHOOLS.map((school) => ({ stat: 'damageTakenPct', value, school }));

/** A stun debuff (trash control; bosses ignore CC through their own immunities). */
export function stun(id: AuraId, name: string, durationMs: Ms): AuraDef {
  return { id, name, kind: 'debuff', durationMs, stacking: 'refresh', cc: 'stun' };
}
